import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useToday } from '@/hooks/useToday'
import { useVisibleTasks } from '@/hooks/useVisibleTasks'
import { datesSummary } from '@/logic/labels'
import { isOpen } from '@/logic/status'
import { activeFilterCount } from '@/logic/filters'
import { usePrefs } from '@/store/prefs'
import { setSearchOpen, useUi } from '@/store/ui'
import { Sheet } from './Sheet'
import { LockIcon } from './icons'
import './SearchSheet.css'

/**
 * Search is search only: a flat list of results across every date, open tasks
 * and those ticked in the last 30 days, each with its dates. It follows the
 * filters that are on. Tapping a result opens the task.
 */
export function SearchSheet() {
  const open = useUi((s) => s.searchOpen)
  const navigate = useNavigate()
  const today = useToday()
  const tasks = useVisibleTasks()
  const filtered = usePrefs((s) => activeFilterCount(s.filter) > 0)
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  const results = useMemo(() => {
    if (!q) return []
    return tasks
      .filter((t) => t.title.toLowerCase().includes(q) || (t.description ?? '').toLowerCase().includes(q))
      .sort((a, b) => Number(isOpen(b)) - Number(isOpen(a)) || a.title.localeCompare(b.title))
      .slice(0, 50)
  }, [tasks, q])
  const close = () => setSearchOpen(false)
  return (
    <Sheet open={open} onClose={close} heading="Search">
      <div className="search">
        <label htmlFor="search-q" className="sr-only">
          Search every date
        </label>
        <input id="search-q" className="search__input" type="search" placeholder="Search every date" autoFocus autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} />
        {filtered && <p className="search__note">Results follow the filters that are on.</p>}
        {q && results.length === 0 && <p className="search__note">Nothing matches “{query.trim()}”.</p>}
        {results.length > 0 && (
          <ul className="search__results">
            {results.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className={`search__result ${isOpen(t) ? '' : 'search__result--done'}`}
                  onClick={() => {
                    close()
                    navigate(`/task/${t.id}`)
                  }}
                >
                  <span className="search__title">
                    {t.title}
                    {t.scope === 'private' && <LockIcon size={13} title="Private" />}
                  </span>
                  <small>{datesSummary(t, today)}</small>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  )
}
